import React, {useMemo} from 'react';
import {motion} from 'framer-motion';
import {classNames} from 'utils';
import {isDesktop} from 'react-device-detect';

type ButtonVariantType = 'primary' | 'secondary' | 'alert';
type ButtonStateType = ButtonVariantType | 'disabled' | 'loading';

const BUTTON_STATE_MAP: Record<ButtonStateType, string> = {
  primary: classNames('bg-primary active:brightness-110 border border-transparent active:scale-95', isDesktop && 'hover:brightness-115'),
  secondary: classNames(
    'bg-white dark:bg-darkBg active:brightness-110 border border-primary dark:border-darkText active:scale-95',
    isDesktop && 'hover:bg-slate-100 dark:hover:bg-slate-700',
  ),
  alert: classNames('bg-red-500 active:bg-red-400 border border-transparent active:scale-95', isDesktop && 'hover:bg-red-400'),
  disabled: 'bg-slate-400 border border-transparent',
  loading: 'bg-slate-400 border border-transparent',
};

const BUTTON_TEXT_STATE_MAP: Record<ButtonStateType, string> = {
  primary: 'text-white',
  secondary: 'text-primary dark:text-darkText',
  alert: 'text-white',
  disabled: 'text-white',
  loading: 'text-white',
};

interface IButtonProps {
  text: string;
  type?: 'button' | 'submit';
  onClick?: () => void;
  variant?: ButtonVariantType;
  size?: 'normal' | 'small' | 'extra-small';
  tooltip?: string;
  disabled?: boolean;
  loading?: boolean;
  rounded?: boolean | string;
  fullWidth?: boolean;
}

const Button: React.FC<IButtonProps> = ({
  text,
  type = 'button',
  onClick,
  variant = 'primary',
  size = 'normal',
  tooltip = '',
  disabled = false,
  loading = false,
  rounded = false,
  fullWidth = false,
}) => {
  const state: ButtonStateType = useMemo(() => {
    if (loading) return 'loading';
    if (disabled) return 'disabled';
    return variant;
  }, [loading, disabled, variant]);

  return (
    <button
      title={tooltip}
      type={type}
      onClick={() => onClick?.()}
      disabled={loading || disabled}
      className={classNames(
        'flex shrink-0 justify-center items-center transition-all duration-200 select-none',
        typeof rounded === 'string' ? rounded : rounded ? 'rounded-full' : 'rounded-md',
        fullWidth && 'w-full',
        size === 'normal' && 'px-10 py-2',
        size === 'small' && 'px-8 py-1.5',
        size === 'extra-small' && 'px-4 py-1.5',
        loading || disabled ? 'cursor-default' : 'cursor-pointer',
        BUTTON_STATE_MAP[state],
      )}>
      <p
        className={classNames(
          'text-center font-semibold',
          size === 'normal' && 'text-md',
          size === 'small' && 'text-sm/6',
          size === 'extra-small' && 'text-xs',
          BUTTON_TEXT_STATE_MAP[state],
          loading && 'opacity-0',
        )}>
        {text}
      </p>
      {loading && (
        <motion.div
          className="absolute flex justify-center items-center w-5 h-5 bg-slate-200 rounded-full"
          animate={{rotate: 360}}
          transition={{repeat: Infinity, duration: 1, ease: 'linear'}}>
          <div className={`w-[80%] h-[80%] rounded-full ${BUTTON_STATE_MAP['loading']}`}>
            <div className={`w-[25%] h-[25%] rounded-md ${BUTTON_STATE_MAP['loading']}`}></div>
          </div>
        </motion.div>
      )}
    </button>
  );
};

export default Button;
